import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ApiService } from '../../../../../core/services/api.service';
import { FileVersionEntry, FileStatus } from '../../../../../core/models/file-browser.models';

interface VersionRow extends FileVersionEntry {
  status: FileStatus | null;
  statusLoading: boolean;
}

@Component({
  selector: 'app-file-history-dialog',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">

        <!-- Header -->
        <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Version history</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{{ filename }}</p>
          </div>
          <button (click)="close.emit()" class="ml-4 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto">
          @if (loading) {
            <div class="flex items-center justify-center py-16 text-gray-400">
              <svg class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              Loading versions…
            </div>
          } @else if (error) {
            <div class="m-6 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
              {{ error }}
            </div>
          } @else if (!rows.length) {
            <p class="px-6 py-12 text-center text-sm text-gray-400">No versions found.</p>
          } @else {
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th class="px-6 py-2.5 text-left font-medium">Version ID</th>
                  <th class="px-4 py-2.5 text-left font-medium w-40">Date</th>
                  <th class="px-4 py-2.5 text-right font-medium w-24">Size</th>
                  <th class="px-4 py-2.5 text-center font-medium w-28">Status</th>
                </tr>
              </thead>
              <tbody>
                @for (row of rows; track row.versionId) {
                  <tr class="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td class="px-6 py-3">
                      <div class="flex items-center gap-2">
                        <span class="font-mono text-xs text-gray-600 dark:text-gray-400 truncate max-w-[200px]" [title]="row.versionId">
                          {{ row.versionId | slice:0:16 }}…
                        </span>
                        @if (row.isLatest) {
                          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 flex-shrink-0">
                            current
                          </span>
                        }
                      </div>
                    </td>
                    <td class="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                      {{ row.lastModified | date:'MMM d, y, HH:mm' }}
                    </td>
                    <td class="px-4 py-3 text-right text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                      {{ formatBytes(row.size) }}
                    </td>
                    <td class="px-4 py-3 text-center">
                      @if (row.statusLoading) {
                        <span class="inline-block w-16 h-5 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></span>
                      } @else {
                        @switch (row.status) {
                          @case ('INGESTED') {
                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Ingested</span>
                          }
                          @case ('PARSED') {
                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">Parsed</span>
                          }
                          @case ('UPLOADED') {
                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">Uploaded</span>
                          }
                          @default {
                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">—</span>
                          }
                        }
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>

        <!-- Footer -->
        <div class="flex justify-end px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <button
            (click)="close.emit()"
            class="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  `,
})
export class FileHistoryDialogComponent implements OnInit {
  @Input() s3Key = '';
  @Input() filename = '';
  @Output() close = new EventEmitter<void>();

  private api = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  rows: VersionRow[] = [];
  loading = true;
  error: string | null = null;

  ngOnInit(): void {
    this.api.getFileVersions(this.s3Key).pipe(
      switchMap(versions => {
        if (!versions.length) return of({ versions, statuses: [] });
        const refs = versions.map(v => ({ s3Key: this.s3Key, versionId: v.versionId }));
        return this.api.getFileStatuses(refs).pipe(
          catchError(() => of([])),
          switchMap(statuses => of({ versions, statuses }))
        );
      }),
      catchError(err => {
        this.error = err?.message || 'Failed to load version history';
        this.loading = false;
        this.cdr.markForCheck();
        return of(null);
      })
    ).subscribe(result => {
      if (!result) return;
      const statusMap = new Map(result.statuses.map(s => [s.versionId, s.status]));
      this.rows = result.versions.map(v => ({
        ...v,
        status: statusMap.get(v.versionId) ?? null,
        statusLoading: false,
      }));
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  }
}
